/**
 * Transaction and selection helpers of the motion columns (W2-04a): run a core command body in one undoable
 * transaction with toasts for its notes and failure reason, snap the playhead to the grid, read the selection.
 */

import type { MessageRef } from '../../core/util/types';
import type { KeyId, TrackValue } from '../../core/model/types';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import { Defaults } from '../../core/model/defaults';
import type { TxBuilder } from '../../core/ops/types';
import type { AppServices, ToastOptions } from '../../app/registry/types';
import { snapBeat, FINEST_DIV } from '../../render/timeline/axis';
import type { ColumnRef, DocStore, Selection, TxnMeta } from '../../state/types';
import { m } from './motion.messages';
import type { PropContext } from './format';

export type Services = Pick<
  AppServices,
  | 'store'
  | 'engine'
  | 'editor'
  | 'ui'
  | 'i18n'
  | 'audio'
  | 'settings'
  | 'registry'
  | 'timeline'
  | 'clipboard'
  | 'keymap'
>;

/** A MessageRef from a key of this package's catalogue. */
export function ref(key: string, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

/**
 * Core notes that describe an inexact shape-preserving split (the toast offers "Bake"; DESIGN.md §5.6.1: Expo /
 * Elastic curves, text type-on / erase / extend / counter segments and colour segments whose split value is not
 * exact).
 */
export const SPLIT_NOTES: ReadonlySet<string> = new Set([
  'cmdMotion.splitApprox',
  'cmdMotion.splitApproxText',
  'cmdMotion.splitApproxColor',
  'cmdMotion.splitBaked',
  'cmdMotion.splitBakedText',
  'cmdMotion.splitPartner',
]);

/** Text split notes whose measured shift is 0 ms: the characters still appear at the same moments. */
const TEXT_SPLIT_NOTES: ReadonlySet<string> = new Set(['cmdMotion.splitApproxText', 'cmdMotion.splitBakedText']);

/**
 * Whether a note's toast offers "Bake": an inexact split, except a text split whose measured shift is 0 ms (baking
 * cannot improve its timing, CR-W1-10b-17).
 */
export function offersBake(note: MessageRef): boolean {
  if (!SPLIT_NOTES.has(note.key)) return false;
  return !(TEXT_SPLIT_NOTES.has(note.key) && note.params?.ms === 0);
}

export interface RunOptions {
  /** Do not toast the transaction's notes (the caller shows its own). */
  readonly quietNotes?: boolean;
  /** Extra toast after a successful commit. */
  readonly success?: { readonly msg: MessageRef; readonly opts?: ToastOptions };
}

export type RunResult<R> =
  | { readonly ok: true; readonly value: R; readonly notes: readonly MessageRef[] }
  | { readonly ok: false; readonly reason: MessageRef | null };

/** Runs `build` in one transaction; toasts the failure reason (error) or the notes (info, Bake for split notes). */
export function runTx<R>(s: Services, meta: TxnMeta, build: (tx: TxBuilder) => R, opts: RunOptions = {}): RunResult<R> {
  const store = s.store;
  if (store === null) return { ok: false, reason: null };
  const out = store.transact(meta, build);
  if (!out.ok) {
    s.ui.toast(out.reason, { kind: 'error' });
    return { ok: false, reason: out.reason };
  }
  if (opts.quietNotes !== true) {
    for (const note of out.notes) {
      const bake = offersBake(note);
      s.ui.toast(
        note,
        bake ? { kind: 'info', action: { label: ref(m.bakeAction), command: 'segments.bake' } } : { kind: 'info' },
      );
    }
  }
  if (opts.success !== undefined) s.ui.toast(opts.success.msg, opts.success.opts ?? { kind: 'success' });
  return { ok: true, value: out.value, notes: out.notes };
}

/** The current snap divisor (1/1920 when beat snap is off). */
export function snapDiv(s: Pick<AppServices, 'editor'>): number {
  const snap = s.editor.getState().snap;
  return snap.beatSnap ? snap.div : FINEST_DIV;
}

/** Playhead chart second (0 without a transport). */
export function playheadSec(s: Pick<AppServices, 'audio'>): number {
  try {
    return s.audio.transport.chartTime();
  } catch {
    return 0;
  }
}

/**
 * The playhead as an exact Beat on the snap grid. When paused and the playhead was off the grid, it is moved onto
 * the grid (DESIGN.md §5.6.6: the playhead visibly snaps when a keyframe is written, so the line does not jump).
 */
export function snappedPlayhead(s: Pick<AppServices, 'audio' | 'engine' | 'store' | 'editor'>, seek = true): Beat {
  const engine = s.engine;
  const sec = playheadSec(s);
  const f = engine === null ? sec : engine.tempo.beatF64At(sec);
  const beat = snapBeat(Math.max(0, f), snapDiv(s));
  if (seek && engine !== null) {
    const playing = isPlaying(s);
    const target = engine.tempo.secAt(beat);
    if (!playing && Math.abs(target - sec) > 1e-9) {
      try {
        s.audio.transport.seek(target);
      } catch {
        // No transport: the key is still written at the snapped beat.
      }
    }
  }
  return beat;
}

function isPlaying(s: Pick<AppServices, 'audio'>): boolean {
  try {
    return s.audio.transport.state.playing;
  } catch {
    return false;
  }
}

/** Selected keys that still exist. */
export function selectedKeys(store: DocStore, sel: Selection): KeyId[] {
  return [...sel.keys].filter((id) => store.doc.keys.has(id));
}

/** Selected segments that still exist and have an end key. */
export function selectedSegments(store: DocStore, sel: Selection): KeyId[] {
  return [...sel.segments].filter((id) => store.doc.keys.has(id) && store.idx.nextKey(id) !== undefined);
}

/** Segments to act on: the selected segments, else the outgoing segments of the selected keys. */
export function segmentsOrKeys(store: DocStore, sel: Selection): KeyId[] {
  const segs = selectedSegments(store, sel);
  if (segs.length > 0) return segs;
  return selectedKeys(store, sel).filter((id) => store.idx.nextKey(id) !== undefined);
}

/** Keys to act on: the selected keys plus the start and end keys of selected segments. */
export function keysOrSegments(store: DocStore, sel: Selection): KeyId[] {
  const out = new Set(selectedKeys(store, sel));
  for (const id of selectedSegments(store, sel)) {
    out.add(id);
    const next = store.idx.nextKey(id);
    if (next !== undefined) out.add(next);
  }
  return [...out];
}

/** The value a NEW track of a property starts with: what renders like the absent track where one exists. */
export function newTrackValue(prop: ColumnRef['prop'], layer: number, ctx: PropContext): TrackValue {
  if (prop === 'color') return [255, 255, 255];
  if (prop === 'text') return '';
  const v = Defaults.leadInValue(prop, layer, ctx.kind, ctx.uiMover);
  if (v !== null) return v;
  if (prop === 'gif') return 0;
  return 1;
}

/** Beat label for toasts ("12:1/4"). */
export function beatLabel(b: Beat): string {
  if (b.n === 0) return String(b.w);
  return `${String(b.w)}:${String(b.n)}/${String(b.d)}`;
}

/** f64 of a Beat (re-export for brevity). */
export function f64(b: Beat): number {
  return Beats.toF64(b);
}
